import React, { useState, useEffect } from "react";
import Navbar from "./Navbar";
import Footer from "./header and footer/Footer";
import "./css/style.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookOpen,
  faChevronDown,
  faScaleBalanced,
  faUserShield,
  faGavel,
  faFileShield,
  faHandHoldingHeart,
  faMoneyBillTransfer,
  faEyeSlash,
  faLaptopCode,
  faGraduationCap,
  faLightbulb
} from "@fortawesome/free-solid-svg-icons";

function AccordionItem({ icon, title, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div className={`accordion-item ${isOpen ? "open" : ""}`}>
      <button className="accordion-header" onClick={() => setIsOpen(!isOpen)}>
        <h3>
          <FontAwesomeIcon icon={icon} style={{ color: "#2563eb" }} />
          {title}
        </h3>
        <FontAwesomeIcon icon={faChevronDown} className="chevron" />
      </button>
      <div className="accordion-body">
        <div className="accordion-body-inner">{children}</div>
      </div>
    </div>
  );
}

function Resources() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".reveal, .reveal-stagger").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <Navbar page={"resources"} />

      <div className="page-banner">
        <h1>Resources & Policies</h1>
        <p>Access our learning resources, governance documents, and institutional policies.</p>
      </div>

      {/* eLearning Resources */}
      <section style={{ padding: "80px 8%" }}>
        <div className="reveal" style={{ textAlign: "center", marginBottom: "48px" }}>
          <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "2.2rem", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>
            eLearning Resources
          </h2>
          <p style={{ color: "#64748b", fontSize: "1.05rem", maxWidth: "600px", margin: "0 auto" }}>
            Tools and guides to maximize your learning experience
          </p>
        </div>

        <div className="accred-grid reveal-stagger">
          <div className="accred-card">
            <div className="accred-icon"><FontAwesomeIcon icon={faBookOpen} /></div>
            <h3>Learning Guides</h3>
            <p>Comprehensive study guides, learning strategies, and tips from our expert instructors to help you succeed in every course you undertake.</p>
          </div>
          <div className="accred-card">
            <div className="accred-icon" style={{ background: "#e0f2fe", color: "#0284c7" }}><FontAwesomeIcon icon={faLaptopCode} /></div>
            <h3>Technical Documentation</h3>
            <p>Detailed technical references, coding tutorials, and lab manuals for hands-on practice in computer science, engineering, and IT courses.</p>
          </div>
          <div className="accred-card">
            <div className="accred-icon" style={{ background: "#fef3c7", color: "#d97706" }}><FontAwesomeIcon icon={faGraduationCap} /></div>
            <h3>Academic Resources</h3>
            <p>Research papers, academic journals, case studies, and supplementary reading materials curated by our faculty for deeper subject exploration.</p>
          </div>
          <div className="accred-card">
            <div className="accred-icon" style={{ background: "#d1fae5", color: "#059669" }}><FontAwesomeIcon icon={faLightbulb} /></div>
            <h3>Career Toolkit</h3>
            <p>Resume templates, interview preparation guides, portfolio building tips, and career development resources to help you succeed professionally.</p>
          </div>
        </div>
      </section>

      {/* Policies & Governance */}
      <section style={{ padding: "80px 8%", background: "#f8fafc" }}>
        <div className="reveal" style={{ textAlign: "center", marginBottom: "48px" }}>
          <h2 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "2.2rem", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>
            Policies & Governance
          </h2>
          <p style={{ color: "#64748b", fontSize: "1.05rem", maxWidth: "600px", margin: "0 auto" }}>
            Transparency and accountability in everything we do
          </p>
        </div>

        <div style={{ maxWidth: "800px", margin: "0 auto" }} className="reveal">
          <AccordionItem icon={faScaleBalanced} title="Code of Ethics" defaultOpen={true}>
            <p>Our Code of Ethics establishes the moral and professional standards that govern the behavior of all stakeholders within the Consortium eLearning community. We are committed to honesty, integrity, respect, and fairness in all our academic and professional interactions.</p>
            <p style={{ marginTop: "12px" }}>All instructors, students, and partners are expected to uphold these principles, fostering an environment of mutual respect, academic excellence, and ethical conduct in every aspect of our digital learning ecosystem.</p>
          </AccordionItem>

          <AccordionItem icon={faUserShield} title="Behavior Policy">
            <p>The Behavior Policy outlines acceptable conduct standards for all community members. We maintain a zero-tolerance policy towards harassment, discrimination, bullying, and any form of disrespectful behavior within our platform.</p>
            <p style={{ marginTop: "12px" }}>Violations may result in temporary suspension or permanent removal from the platform. We encourage learners and instructors to report any concerns through our confidential reporting system.</p>
          </AccordionItem>

          <AccordionItem icon={faHandHoldingHeart} title="Statement of Impartiality">
            <p>Consortium eLearning is committed to impartiality in all our operations, assessments, and certifications. We ensure that all decisions are made objectively, free from bias, commercial pressure, or external influence.</p>
            <p style={{ marginTop: "12px" }}>Our assessment processes are designed to be fair and equitable, providing equal opportunities for all learners regardless of their background, location, or personal circumstances.</p>
          </AccordionItem>

          <AccordionItem icon={faGavel} title="Complaint & Appeal Policy">
            <p>We take all complaints seriously and have established a formal process for handling grievances. Students and instructors can submit complaints through our dedicated portal, and all cases are investigated thoroughly within 10 business days.</p>
            <p style={{ marginTop: "12px" }}>Our appeals process provides a fair opportunity to challenge assessment results, disciplinary actions, or any other decisions. All appeals are reviewed by an independent panel to ensure objectivity and fairness.</p>
          </AccordionItem>

          <AccordionItem icon={faFileShield} title="Digital Trust">
            <p>We are committed to maintaining the highest standards of digital trust and cybersecurity. All personal data is encrypted, stored securely, and processed in compliance with international data protection regulations including GDPR.</p>
            <p style={{ marginTop: "12px" }}>Our platform undergoes regular security audits, penetration testing, and compliance reviews to ensure the safety and privacy of our community members' information.</p>
          </AccordionItem>

          <AccordionItem icon={faMoneyBillTransfer} title="Refund Policy">
            <p>We offer a comprehensive refund policy to ensure student satisfaction. Course refunds are available within 14 days of purchase if less than 30% of the course content has been accessed. Refund requests are processed within 5-7 business days.</p>
            <p style={{ marginTop: "12px" }}>For subscription-based plans, cancellation is available at any time with prorated refunds for unused months. Special circumstances are reviewed on a case-by-case basis by our student support team.</p>
          </AccordionItem>

          <AccordionItem icon={faEyeSlash} title="Privacy & Fraud Reporting">
            <p>Our Privacy Policy details how we collect, use, store, and protect personal information. We never sell personal data to third parties, and all data processing is conducted in accordance with applicable privacy laws and regulations.</p>
            <p style={{ marginTop: "12px" }}>We maintain a dedicated fraud reporting channel for reporting suspicious activities, academic dishonesty, or unauthorized use of certificates. All reports are treated confidentially and investigated promptly by our compliance team.</p>
          </AccordionItem>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Resources;
